import { useMemo, useState, type ReactNode } from 'react';
import '@/styles/pages.css';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CLAIM_STATUSES,
  fmtDate,
  money,
  periodLabel,
  statusLabel,
  type ClaimStatus,
} from '@gdec/shared';
import { api } from '@/api/client';
import { useCurrentUser } from '@/auth/AuthContext';
import { StatusBadge } from '@/components/Badge';
import { ClaimViewer } from '@/components/ClaimViewer';
import { DataTable, type Column } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { LoadingBlock } from '@/components/Spinner';
import { useAsync } from '@/hooks/useAsync';
import type { MyClaimRow } from '@/types/claims';

interface DraftRow {
  id: string;
  period: string;
  rowCount: number;
  draftTotal: number;
  updatedAt: number;
}

/**
 * Every claim this user has submitted, in any state.
 *
 * The `?claim=` search param opens one directly, so a link from the dashboard or
 * from a notification lands on the claim rather than on the list.
 */
export function MyRequestsPage(): ReactNode {
  const me = useCurrentUser();
  const [params, setParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<ClaimStatus | ''>('');
  const [search, setSearch] = useState('');

  const { data, loading, error, reload } = useAsync<MyClaimRow[]>(
    () => api.claims.mine<MyClaimRow[]>(),
    [],
  );
  const drafts = useAsync<DraftRow[]>(() => api.drafts.list<DraftRow[]>(), []);

  const openClaimId = params.get('claim');
  const closeViewer = () => {
    params.delete('claim');
    setParams(params, { replace: true });
  };

  const claims = useMemo(() => {
    const all = data ?? [];
    const needle = search.trim().toLowerCase();
    return all.filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (!needle) return true;
      return c.id.toLowerCase().includes(needle) || periodLabel(c.period).toLowerCase().includes(needle);
    });
  }, [data, statusFilter, search]);

  if (loading) return <LoadingBlock label="Loading your requests…" />;
  if (error) {
    return (
      <EmptyState
        icon="⚠️"
        title="Could not load your requests."
        description={error}
        action={
          <button type="button" className="btn btn-ghost" onClick={reload}>
            Try again
          </button>
        }
      />
    );
  }

  const columns: ReadonlyArray<Column<MyClaimRow>> = [
    {
      key: 'id',
      header: 'Request ID',
      cell: (r) => (
        <button type="button" className="link-btn" onClick={() => openClaim(r.id)}>
          {r.id}
        </button>
      ),
    },
    { key: 'period', header: 'Date Covered', cell: (r) => periodLabel(r.period) },
    { key: 'rows', header: 'Receipts', align: 'right', cell: (r) => r.rowCount },
    { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
    { key: 'claimed', header: 'Claimed', align: 'right', cell: (r) => money(r.claimedAmount) },
    {
      key: 'approved',
      header: 'Approved',
      align: 'right',
      // Since Round 2 approving releases the claim at the reviewed amount and
      // final_approved_amount is never written; only historic claims carry it.
      cell: (r) => {
        const approved =
          r.finalApprovedAmount ?? (r.status === 'Released' ? r.financeReviewedAmount : null);
        return approved == null ? '—' : money(approved);
      },
    },
    {
      key: 'released',
      header: 'Released',
      align: 'right',
      cell: (r) => (r.releasedAmount == null ? '—' : money(r.releasedAmount)),
    },
    { key: 'submitted', header: 'Submitted', cell: (r) => fmtDate(r.createdAt) },
    {
      key: 'action',
      header: '',
      cell: (r) =>
        r.status === 'Disapproved' ? (
          <Link className="btn btn-gold btn-sm" to={`/claim-new?resubmit=${r.id}`}>
            Edit &amp; resubmit
          </Link>
        ) : null,
    },
  ];

  function openClaim(id: string): void {
    params.set('claim', id);
    setParams(params, { replace: true });
  }

  const all = data ?? [];

  return (
    <>
      {(drafts.data?.length ?? 0) > 0 ? (
        <div className="panel">
          <h3>
            Saved Drafts
            <span className="tag-chip">{drafts.data?.length}</span>
          </h3>
          <p className="hint">
            Not submitted, and visible only to you. Nobody is waiting on these.
          </p>
          <DataTable
            rows={drafts.data ?? []}
            columns={[
              { key: 'period', header: 'Date Covered', cell: (d) => periodLabel(d.period) },
              { key: 'rows', header: 'Receipts', align: 'right', cell: (d) => d.rowCount },
              {
                key: 'total',
                header: 'Draft Total',
                align: 'right',
                cell: (d) => money(d.draftTotal),
              },
              { key: 'saved', header: 'Last Saved', cell: (d) => fmtDate(d.updatedAt) },
              {
                key: 'actions',
                header: '',
                cell: (d) => (
                  <Link className="btn btn-primary btn-sm" to={`/claim-new?draft=${d.id}`}>
                    Continue
                  </Link>
                ),
              },
            ]}
            rowKey={(d) => d.id}
          />
        </div>
      ) : null}

      <div className="panel">
        <h3>
          My Requests
          <div className="actions">
            <Link className="btn btn-primary btn-sm" to="/claim-new">
              🧾 New Request
            </Link>
          </div>
        </h3>

        <div className="filter-row">
          <div className="field">
            <label htmlFor="mr-search">Search</label>
            <input
              id="mr-search"
              type="search"
              placeholder="Request ID or period"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="mr-status">Status</label>
            <select
              id="mr-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ClaimStatus | '')}
            >
              <option value="">All statuses</option>
              {CLAIM_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {statusLabel(s)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <DataTable
          rows={claims}
          columns={columns}
          rowKey={(r) => r.id}
          empty={
            all.length === 0 ? (
              <EmptyState
                icon="🧾"
                title="You have not submitted a claim yet."
                description="Start a new expense claim request and your receipts will be read for you."
                action={
                  <Link className="btn btn-primary" to="/claim-new">
                    New Expense Claim Request
                  </Link>
                }
              />
            ) : (
              <EmptyState icon="🔍" title="No claim matches those filters." />
            )
          }
        />
      </div>

      {openClaimId ? (
        // Every claim on this page is the caller's own, so the requester's name
        // is already known — passing it fills the header on the first paint
        // rather than showing a dash until `claims.get` returns.
        <ClaimViewer claimId={openClaimId} requesterName={me.name} onClose={closeViewer} />
      ) : null}
    </>
  );
}
